import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "../styles/Apartments.css";
import api from "../apiService/api";

const API_URL = api.API_URL + "read_apartments&limit=3&page=1";
const BASE_URL = api.BASE_URL;

const Apartments = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const [page, setPage] = useState(1);

  const [apartments, setApartments] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [apartmentsPerPage, setApartmentsPerPage] = useState(3);
  const [selectedApartment, setSelectedApartment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Reservation form states
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [name, setName] = useState(""); // Added name state
  const [reservationStatus, setReservationStatus] = useState("idle");
  const [reservationError, setReservationError] = useState(null);

  const [selectedMediaImage, setSelectedMediaImage] = useState({
    index: null,
    image: null,
  });
  const [loadingImages, setLoadingImages] = useState(
    selectedApartment ? new Array(selectedApartment.apartment_images.length).fill(true) : []
  );
  

  const handleImageLoad = (index) => {
    setLoadingImages((prevLoading) => {
      const newLoading = [...prevLoading];
      newLoading[index] = false; // Mark image as loaded
      return newLoading;
    });
  };
  const handleLightboxNav = (direction) => {
    const totalImages = selectedApartment.apartment_images.length;
    let newIndex;

    if (direction === "next") {
      newIndex = (selectedMediaImage.index + 1) % totalImages;
    } else if (direction === "prev") {
      newIndex = (selectedMediaImage.index - 1 + totalImages) % totalImages;
    }

    setSelectedMediaImage({
      index: newIndex,
      image: selectedApartment.apartment_images[newIndex].filePath,
    });
  };

useEffect(() => {
  const fetchApartments = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${BASE_URL}index.php?action=read_apartment&limit=3&page=${page}`
      );
      const data = await response.json();

      if (data.success && Array.isArray(data.apartments)) {
        setApartments((prevApartments) => {
          // ✅ If first fetch (page 1), reset list
          if (page === 1) {
            return data.apartments.map((apartment) => ({
              id: apartment.id,
              apartment_name: apartment.apartment_name,
              location: apartment.location,
              price: apartment.price,
              description: apartment.description,
              coverImage: apartment.cover_image || "", // ✅ Ensure coverImage is always defined
              apartment_images: apartment.apartment_images
                ? apartment.apartment_images.map((image) => ({
                    filePath: image.image_path || "", // ✅ Ensure image path is valid
                  }))
                : [],
            }));
          }

          // ✅ Otherwise, append new unique apartments only
          const newApartments = data.apartments
            .filter((apartment) => !prevApartments.some((a) => a.id === apartment.id))
            .map((apartment) => ({
              id: apartment.id,
              apartment_name: apartment.apartment_name,
              location: apartment.location,
              price: apartment.price,
              description: apartment.description,
              coverImage: apartment.cover_image || "", // ✅ Ensure coverImage is always valid
              apartment_images: apartment.apartment_images
                ? apartment.apartment_images.map((image) => ({
                    filePath: image.image_path || "", // ✅ Ensure image path is valid
                  }))
                : [],
            }));

          return [...prevApartments, ...newApartments];
        });
      } else {
        setError("");
      }
    } catch (error) {
      setError("");
    } finally {
      setLoading(false);
    }
  };

  fetchApartments();
}, [page]);

  useEffect(() => {
    if (selectedApartment) {
      setLoadingImages(new Array(selectedApartment.apartment_images.length).fill(true));
    }
  }, [selectedApartment]);

  // Adjust apartments per page based on screen size
  useEffect(() => {
    const updateApartmentsPerPage = () => {
      const width = window.innerWidth;
      if (width <= 480) {
        setApartmentsPerPage(1); // Mobile: 1 card
      } else if (width <= 768) {
        setApartmentsPerPage(1); // Tablet: 1 card for simplicity
      } else if (width <= 1024) {
        setApartmentsPerPage(2); // Larger tablets: 2 cards
      } else {
        setApartmentsPerPage(3); // Desktop: 3 cards
      }
    };

    updateApartmentsPerPage();
    window.addEventListener("resize", updateApartmentsPerPage);

    return () => window.removeEventListener("resize", updateApartmentsPerPage);
  }, []);

  const scrollLeft = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? apartments.length - apartmentsPerPage : prevIndex - 1
    );
  };

  const scrollRight = () => {
    setPage((prevPage) => prevPage + 1); 
   
    setCurrentIndex((prevIndex) =>
      prevIndex >= apartments.length - apartmentsPerPage ? 0 : prevIndex + 1
    );
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setPhoneNumber(value);
  };

  const handleReservationSubmit = async (e) => {
    e.preventDefault();

    if (!selectedApartment) return;

    setReservationStatus("submitting");
    setReservationError(null);

    try {
      const response = await fetch(
        // "https://api.kasbah-magic.com/conciergeLastVersion/index.php?action=create_apartment_reservation",
        `${BASE_URL}index.php?action=create_apartment_reservation`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            apartment_id: selectedApartment.id,
            start_date: startDate,
            end_date: endDate,
            phone_number: phoneNumber,
            name: name, // Added name to payload
            status: "pending",
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        setReservationStatus("success");
        setStartDate("");
        setEndDate("");
        setPhoneNumber("");
        setName(""); // Reset name field
      } else {
        setReservationStatus("error");
        setReservationError(data.message || "Failed to submit reservation");
      }
    } catch (error) {
      setReservationStatus("error");
      setReservationError("Network error. Please try again.");
    }
  };

  // if (loading) return <p>Loading apartments...</p>;
  if (error) return <p style={{ color: "red" }}>{error}</p>;

  return (
    <div className="apartments-container">
      <div className="Apheader-container">
        <h1 className="apartments-title">{t("apartmentsTitle")}</h1>
        <div className="navigation-buttons">
          <button className="Apnav-btn" onClick={isArabic ? scrollRight : scrollLeft}>
            {isArabic ? "→" : "←"}
          </button>
          <button className="Apnav-btn" onClick={isArabic ? scrollLeft : scrollRight}>
            {isArabic ? "←" : "→"}
          </button>
        </div>
      </div>

      <div className="apartments-grid">
        {apartments.slice(currentIndex, currentIndex + apartmentsPerPage).map((apartment) => (
          <div key={apartment.id} className="apartment-card">
            <div className="Apimage-container">
              <img src={apartment.coverImage} alt={apartment.apartment_name} className="apartment-image" />
              <div className="Apimage-overlay" />
            </div>
            <div className="apartment-info">
              <div className="text-content">
                <h3 className="apartment-title">{apartment.apartment_name}</h3>
                <p className="apartment-location"><strong>{apartment.location}</strong></p>
                <p className="apartment-price">{apartment.price} MAD</p>
                <p className="apartment-description">{apartment.description}</p>
              </div>
              <button className="learn-more-btn" onClick={() => setSelectedApartment(apartment)}>
                <span className="nav">{isArabic ? "←" : "→"}</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {selectedApartment && (
        <div className="modal-overlay" onClick={() => setSelectedApartment(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setSelectedApartment(null)}>×</button>
            <img src={selectedApartment.coverImage} alt={selectedApartment.apartment_name} className="modal-image" />
            <h2 className="modal-title">{selectedApartment.apartment_name}</h2>
            <p className="modal-location">
              <strong>{t("locationLabel")}</strong> {selectedApartment.location}
            </p>
            <p className="modal-price">
              <strong>{t("priceLabel")}</strong> {selectedApartment.price} MAD
            </p>
            <p className="modal-description">{selectedApartment.description}</p>

            {selectedApartment.apartment_images.length > 0 && (
              // <div className="media-gallery">
              //   {selectedApartment.apartment_images.map((image, index) => (
              //   // TODO ADD SHIMMER HERE <div className="shimmer"></div> when image are still loading how to check loading of iñages to settrue false to show shimmer or image 
              //   <img
              //       key={index}
              //       src={image.filePath}
              //       alt="Apartment media"
              //       className="media-image"
              //       onClick={() => setSelectedMediaImage({ index, image: image.filePath })}
              //     />
                  
              //   ))}
                
              // </div>
              <div className="media-gallery">
                {selectedApartment.apartment_images.map((image, index) => (
                  <div key={index} className="media-container">
                    {/* Shimmer effect */}
                    {loadingImages[index] && <div className="shimmer"></div>}

                    {/* Image with onLoad event */}
                    <img
                      src={image.filePath}
                      alt="Apartment media"
                      className={`media-image ${loadingImages[index] ? "hidden" : ""}`}
                      onLoad={() => handleImageLoad(index)}
                      onClick={() => setSelectedMediaImage({ index, image: image.filePath })}
                    />
                  </div>
                ))}
              </div>
              
            )}
            {/* <div className="shimmer"></div> */}

            {selectedMediaImage.index !== null && (
              <div
                className="lightbox-overlay"
                onClick={() => setSelectedMediaImage({ index: null, image: null })}
              >
                <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="lightbox-Apnav-btn left"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLightboxNav("prev");
                    }}
                  >
                    ←
                  </button>
                  <img src={selectedMediaImage.image} alt="Enlarged media" className="lightbox-image" />
                  <button
                    className="lightbox-Apnav-btn right"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLightboxNav("next");
                    }}
                  >
                    →
                  </button>
                  <button
                    className="close-btn"
                    onClick={() => setSelectedMediaImage({ index: null, image: null })}
                  >
                    ×
                  </button>
                </div>
              </div>
            )}

            <div className="apreservation-section">
              <h3>{t("reservationFormTitle", "Make a Reservation")}</h3>
              {reservationStatus === "success" ? (
                <div className="apreservation-success">
                  <p>{t("reservationSuccessMessage", "Your reservation request has been submitted successfully!")}</p>
                  <button
                    className="form-buttonAp"
                    onClick={() => setReservationStatus("idle")}
                    style={{ marginTop: "20px", width: "auto", padding: "0 20px" }}
                  >
                    {t("makeAnotherReservation", "Make Another Reservation")}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleReservationSubmit} className="apreservation-form">
                  <div className="date-inputs">
                    <div className="date-field">
                      <label htmlFor="start-date">{t("checkIn", "Check-In")}</label>
                      <input
                        id="start-date"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="form-dateAp"
                        required
                      />
                    </div>
                    <div className="date-field">
                      <label htmlFor="end-date">{t("checkOut", "Check-Out")}</label>
                      <input
                        id="end-date"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="form-dateAp"
                        required
                      />
                    </div>
                  </div>
                  <div className="phone-field">
                    <label htmlFor="phone-number">{t("phoneNumber", "Phone Number")}</label>
                    <input
                      id="phone-number"
                      type="tel"
                      value={phoneNumber}
                      onChange={handlePhoneChange}
                      className="form-phoneAp"
                      placeholder={t("phonePlaceholder", "Enter your phone number")}
                      required
                    />
                  </div>
                  <div className="phone-field"> {/* Added name field */}
                    <label htmlFor="name">{t("name", "Name")}</label>
                    <input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      // className="form-nameAp"
                      className="form-phoneAp"
                      placeholder={t("namePlaceholder", "Enter your name")}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    className="form-buttonAp"
                    disabled={reservationStatus === "submitting"}
                  >
                    {reservationStatus === "submitting"
                      ? t("submitting", "Submitting...")
                      : t("submitReservation", "Submit Reservation")}
                  </button>
                  {reservationStatus === "error" && (
                    <p className="apreservation-error">{reservationError}</p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Apartments;

